const FAILED_OUTCOMES = ['voicemail', 'no-answer', 'refused']

/**
 * @typedef {object} FailedStreakDetails
 * @property {number} streak - Aantal mislukte pogingen op rij, geteld vanaf de nieuwste
 * @property {{toDate: () => Date}|null} firstFailedAt - Begin van die reeks — wanneer we begonnen te proberen zonder resultaat
 * @property {{toDate: () => Date}|null} lastFailedAt - Meest recente mislukte poging in die reeks
 */

/**
 * Loopt de contactmomenten af vanaf de nieuwste en telt hoeveel er op rij
 * mislukten, plus wanneer die reeks begon.
 *
 * De invoer moet aflopend op Timestamp gesorteerd zijn (nieuwste eerst) —
 * zo levert `useFirestoreGeneralOrderBy(... 'Timestamp', 'desc')` hem aan.
 *
 * Een poging zonder bruikbare Timestamp telt wél mee voor de reeks maar levert
 * geen datum: een net weggeschreven document heeft lokaal nog geen
 * serverTimestamp. Zo'n document mag de reeks niet afbreken en er ook geen
 * startmoment uit verzinnen.
 *
 * @param {Array<{Outcome?: string, Timestamp?: {toDate: () => Date}}>} [contactMoments]
 * @returns {FailedStreakDetails}
 */
const computeFailedStreakDetails = (contactMoments) => {
    const empty = { streak: 0, firstFailedAt: null, lastFailedAt: null }
    if (!Array.isArray(contactMoments)) return empty

    let streak = 0
    let firstFailedAt = null
    let lastFailedAt = null

    for (const item of contactMoments) {
        if (!item || !FAILED_OUTCOMES.includes(item.Outcome)) break
        streak += 1

        if (typeof item.Timestamp?.toDate === 'function') {
            // Aflopend gesorteerd: de eerste bruikbare datum is de meest
            // recente, de laatste die we tegenkomen is het begin van de reeks.
            if (!lastFailedAt) lastFailedAt = item.Timestamp
            firstFailedAt = item.Timestamp
        }
    }

    return { streak, firstFailedAt, lastFailedAt }
}

/**
 * Alleen het aantal mislukte pogingen op rij.
 *
 * Bestaand contract, bewust ongewijzigd — de onbereikbaar-knop en de
 * overzichtskolom rekenen er allebei op.
 * AI-CONTEXT: See docs/pitfalls.md#dev-helper-contract-change-strands-callers
 *
 * @param {Array<object>} contactMoments
 * @returns {number}
 */
const computeFailedStreak = (contactMoments) => computeFailedStreakDetails(contactMoments).streak

export default computeFailedStreak
export { FAILED_OUTCOMES, computeFailedStreakDetails }
